import React, { useMemo } from 'react'
import { trans } from 'sharedKernel/i18n'
import { SolicitationActionMenu } from '../SolicitationActionMenu'
import { IMeetingRoomSolicitationDetail, IMeetingRoomSolicitationRow } from '../types'
import {
  EmptyTableMessage,
  RequesterLink,
  SolicitationsTable,
  SolicitationsTableScroll,
  SolicitationsTableWrapper,
  StatusBadge,
} from './styles'

interface MeetingRoomSolicitationsTableProps {
  rows: IMeetingRoomSolicitationDetail[]
  searchTerm?: string
  onViewDetails?: (solicitation: IMeetingRoomSolicitationDetail) => void
  onEdit?: (solicitation: IMeetingRoomSolicitationDetail) => void
  onApprove?: (solicitation: IMeetingRoomSolicitationDetail) => void
  onReject?: (solicitation: IMeetingRoomSolicitationDetail) => void
  onCancel?: (solicitation: IMeetingRoomSolicitationDetail) => void
}

const filterSolicitations = (
  rows: IMeetingRoomSolicitationDetail[],
  searchTerm: string
): IMeetingRoomSolicitationDetail[] => {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return rows

  return rows.filter(row => {
    const haystack = [
      row.requesterName,
      row.space,
      row.reservationDate,
      row.timeRange,
      row.requesterType,
      row.requestDate,
      row.status,
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(term)
  })
}

const getStatusLabel = (status: IMeetingRoomSolicitationRow['status']): string => {
  switch (status) {
    case 'pendente':
      return trans('meetingRoom.reservations.status.pending')
    case 'aprovada':
      return trans('meetingRoom.reservations.status.approved')
    case 'reprovada':
      return trans('meetingRoom.reservations.status.rejected')
    case 'cancelada':
      return trans('meetingRoom.reservations.status.cancelled')
    default:
      return status
  }
}

const getRequesterTypeLabel = (type: IMeetingRoomSolicitationRow['requesterType']): string => {
  switch (type) {
    case 'externo':
      return trans('meetingRoom.reservations.requesterType.external')
    case 'residente':
      return trans('meetingRoom.reservations.requesterType.resident')
    default:
      return type
  }
}

export const MeetingRoomSolicitationsTable = ({
  rows,
  searchTerm = '',
  onViewDetails,
  onEdit,
  onApprove,
  onReject,
  onCancel,
}: MeetingRoomSolicitationsTableProps): JSX.Element => {
  const filteredRows = useMemo(
    () => filterSolicitations(rows, searchTerm),
    [rows, searchTerm]
  )

  return (
    <SolicitationsTableWrapper>
      <SolicitationsTableScroll>
        <SolicitationsTable responsive>
          <thead>
            <tr>
              <th>{trans('meetingRoom.reservations.column.requesterName')}</th>
              <th>{trans('meetingRoom.reservations.column.space')}</th>
              <th>{trans('meetingRoom.reservations.column.reservationDate')}</th>
              <th>{trans('meetingRoom.reservations.column.time')}</th>
              <th>{trans('meetingRoom.reservations.column.requesterType')}</th>
              <th>{trans('meetingRoom.reservations.column.requestDate')}</th>
              <th>{trans('meetingRoom.reservations.column.status')}</th>
              <th>{trans('meetingRoom.reservations.column.action')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyTableMessage>{trans('meetingRoom.reservations.empty')}</EmptyTableMessage>
                </td>
              </tr>
            ) : (
              filteredRows.map(row => (
                <tr key={row.id}>
                  <td>
                    <RequesterLink
                      type="button"
                      onClick={() => onViewDetails?.(row)}
                    >
                      {row.requesterName}
                    </RequesterLink>
                  </td>
                  <td>{row.space}</td>
                  <td>{row.reservationDate}</td>
                  <td>{row.timeRange}</td>
                  <td>{getRequesterTypeLabel(row.requesterType)}</td>
                  <td>{row.requestDate}</td>
                  <td>
                    <StatusBadge $status={row.status}>{getStatusLabel(row.status)}</StatusBadge>
                  </td>
                  <td>
                    <SolicitationActionMenu
                      row={row}
                      onViewDetails={onViewDetails}
                      onEdit={onEdit}
                      onApprove={onApprove}
                      onReject={onReject}
                      onCancel={onCancel}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </SolicitationsTable>
      </SolicitationsTableScroll>
    </SolicitationsTableWrapper>
  )
}
